import { Navigate, useNavigate } from "react-router-dom";
import { useAuthContext } from "@/auth/AuthContext";
import { OrgUtil } from "@/data/OrgUtil";
import AddButton from "@/components/buttons/AddButton";
import { Switch } from "@/components/ui/switch";
import { User } from "@/api/User";
import OrgChart, { buildOrgChartNodes, OrgChartNode } from "@/components/OrgChart";
import WaitingSpinner from "@/components/WaitingSpinner";
import { StatusUtil } from "@/data/StatusUtil";
import { userService } from "@/service/UserService";
import { Stack } from "@chakra-ui/react";
import Content from "@/components/Content";
import DataTable, { DataTableToolbar } from "@/components/DataTable";
import { useUserColumns } from "@/components/useUserColumns";
import { UserAudit } from "@/api/UserAudit";
import { parseIso } from "@/data/DateTimeUtil";
import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/i18n/I18nContext";
import { CompanyIcon } from "@/components/icons/Icon";

const OrganizationDirectory = ({ canManage }: { canManage: boolean }) => {
    const navigate = useNavigate();
    const { t } = useI18n();
    const [showActive, setShowActive] = useState(true);
    const [isLoading, setIsLoading] = useState(true);
    const [data, setData] = useState<User[]>([]);
    const [audit, setAudit] = useState<Map<number, UserAudit>>(new Map());
    const [reportsTo, setReportsTo] = useState<Map<number, number[]>>(new Map()); // user id as key and list of user ids reporting to

    const loadAllData = (): Promise<void> => {
        setIsLoading(true);
        const p1 = userService.listAll().then(r => {
            setData(r.results.concat());
            setAudit(new Map(r.audit.map(a => [a.userId, { ...a, accessedAt: parseIso(a.accessedAt) }])));
        });
        const p2 = userService.listReportsTo().then(r => setReportsTo(r));
        return Promise.all([p1, p2]).then(_ => { }).finally(() => setIsLoading(false));
    }

    useEffect(() => {
        loadAllData();
    }, []);

    const visibleUsers = useMemo(
        () => canManage && !showActive ? data : data.filter(d => StatusUtil.isStatusActive(d.status)),
        [data, canManage, showActive],
    );
    const onEdit = canManage ? (id: number) => navigate(`/admin/users/edit/${id}`) : undefined;
    const columns = useUserColumns(data, audit, reportsTo, onEdit);
    const orgChartNodes: OrgChartNode[] = useMemo(() => buildOrgChartNodes(visibleUsers, reportsTo), [visibleUsers, reportsTo]);

    return (
        <Content title='Organisation'
            actions={canManage && <AddButton caption='Neuer Benutzer' onClick={() => navigate('/admin/users/add')} />}>
            <Stack gap='6' minW='0'>
                <DataTableToolbar resultCount={isLoading ? undefined : visibleUsers.length}>
                    {canManage && (
                        <Switch id='activeOnly' colorPalette='blue' checked={showActive} onCheckedChange={event => setShowActive(event.checked)}>
                            nur die Aktiven
                        </Switch>
                    )}
                </DataTableToolbar>
                {isLoading ? <WaitingSpinner size='md' mb='1rem' /> : (
                    <>
                        <DataTable columns={columns} data={visibleUsers}
                            initialSortByColumn='access' initialSortByDesc={false} hideColumnsOnXs={['createDate', 'email']}
                            onRowClick={onEdit ? row => onEdit(row.id) : undefined}
                        />
                        {canManage && (
                            <Stack gap='10' align='baseline' direction={{ base: 'column', sm: 'row' }}>
                                <Button variant='outline' rounded="md" onClick={() => navigate('/organization/settings')}><CompanyIcon />{t('organizationSettings.link')}</Button>
                            </Stack>
                        )}
                        <OrgChart
                            nodes={orgChartNodes}
                            organizationId={data[0]?.organizationId ?? -1}
                        />
                    </>
                )}
            </Stack>
        </Content>
    );
}

const Organization = () => {
    const { user } = useAuthContext();
    if (!user) return <Navigate to='/login' replace />;
    if (OrgUtil.isRoleExtern(user.data.role)) return <Navigate to='/' replace />;
    const canManage = OrgUtil.isRoleAdmin(user.data.role) || OrgUtil.isRoleRoot(user.data.role);
    return <OrganizationDirectory key={user.token} canManage={canManage} />;
};

export default Organization;
